import { supabase } from "../lib/supabase";

export type NovaWebsiteContent = {
  brand: {
    city: string;
    phone: string;
    whatsapp: string;
    instagram: string;
    address: string;
    hours1Day: string;
    hours1Time: string;
    hours2Day: string;
    hours2Time: string;
    hours3Day: string;
    hours3Time: string;
  };

  chapters: {
    beginning: string;
    table: string;
    menu: string;
    juice: string;
    reviews: string;
    shop: string;
    gather: string;
    location: string;
  };

  hero: {
    sectionLabel: string;
    titleLine1: string;
    titleAccent: string;
    titleLine3: string;
    copy: string;
    primaryCta: string;
    secondaryCta: string;
    scrollHint: string;
    imageAlt: string;
  };

  table: {
    sectionLabel: string;
    titleLine1: string;
    titleLine2: string;
    copy: string;
    cta: string;
    imageAlt: string;
    imageBadge: string;
  };

  menu: {
    sectionNumber: string;
    marker: string;
    titleLine1: string;
    titleLine2: string;
    intro: string;
    foodTab: string;
    drinksTab: string;
    allTab: string;
    categoryHint: string;
    currentlyBrowsing: string;
    photographyLine1: string;
    photographyLine2: string;
    favouriteLabel: string;
    chooseSizeLabel: string;
    showHighlightsLabel: string;
    showFullMenuLabel: string;
    exploreAllPrefix: string;
    itemsLabel: string;
    footerLabel: string;
    reviewOrderLabel: string;
  };

  juice: {
    sectionLabel: string;
    titleLine1: string;
    titleLine2: string;
    copy: string;
    imageAlt: string;
    offerLabel: string;
    offerCopy: string;
  };

  guestBook: {
    sectionNumber: string;
    marker: string;
    rating: string;
    reviewCount: string;
    reviewCountLabel: string;
    openLabel: string;
    closeLabel: string;
  };

  reviews: Array<{
    quote: string;
    name: string;
    detail: string;
  }>;

  atNova: {
    sectionNumber: string;
    eyebrow: string;
    titleLine1: string;
    titleLine2: string;
    copy: string;
    lead: string;
    body: string;
    cta: string;

    reelLabel: string;
    reelNumber: string;
    reelTitleLine1: string;
    reelTitleLine2: string;
    reelCopy: string;
    reelFooter: string;

    gameLabel: string;
    gameTitleLine1: string;
    gameTitleLine2: string;
    gameCopy: string;

    celebrationLabel: string;
    celebrationTitleLine1: string;
    celebrationTitleLine2: string;
    celebrationCopy: string;

    kitchenLabel: string;
    kitchenCopy: string;

    momentsLabel: string;
    momentsCopy: string;
  };

  reservation: {
    sectionNumber: string;
    eyebrow: string;
    titleLine1: string;
    titleLine2: string;
    copy: string;

    callLabel: string;
    whatsappLabel: string;

    successEyebrow: string;
    successTitle: string;
    successCopy: string;
    successAgain: string;

    formEyebrow: string;
    formTitle: string;
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    dateLabel: string;
    timeLabel: string;
    guestsLabel: string;
    noteLabel: string;
    optionalLabel: string;
    notePlaceholder: string;
    sendingLabel: string;
    submitLabel: string;
  };

  location: {
    sectionNumber: string;
    marker: string;
    titleLine1: string;
    titleLine2: string;
    copy: string;
    address: string;
    locality: string;
    hoursLine1: string;
    hoursLine2: string;
    phone: string;
    mapNameLine1: string;
    mapNameLine2: string;
    mapCoordinates: string;
    mapUrl: string;
    mapCta: string;
  };

  footer: {
    taglineLine1: string;
    taglineLine2: string;
    findUsLabel: string;
    keepInTouchLabel: string;
    instagramUrl: string;
    facebookUrl: string;
    copyright: string;
    closingLine: string;
  };
};

export const DEFAULT_NOVA_WEBSITE_CONTENT: NovaWebsiteContent = {
  brand: {
    city: "Nairobi, Kenya",
    phone: "+254 719 275522",
    whatsapp: "+254 719 275522",
    instagram: "@novaresculina",
    address: "14 Riverside Lane, Westlands",
    hours1Day: "Tue — Thu",
    hours1Time: "12:00 — 21:30",
    hours2Day: "Fri — Sat",
    hours2Time: "12:00 — 22:30",
    hours3Day: "Sunday",
    hours3Time: "12:00 — 18:00",
  },

  chapters: {
    beginning: "The beginning",
    table: "The table",
    menu: "The menu",
    juice: "Juice station",
    reviews: "The guest book",
    shop: "At Nova",
    gather: "Gather here",
    location: "Find us",
  },

  hero: {
    sectionLabel: "Karibu · Nairobi / 01 / arrive",
    titleLine1: "Step",
    titleAccent: "into",
    titleLine3: "Nova.",
    copy:
      "A table in the middle of the story. Leave the pavement behind and move through our house of food, fire, and familiar faces.",
    primaryCta: "Find your table",
    secondaryCta: "Browse the menu",
    scrollHint: "Scroll / move through the house",
    imageAlt:
      "The warm, plant-filled dining room at Nova",
  },

  table: {
    sectionLabel: "02 / settle",
    titleLine1: "Your place",
    titleLine2: "is here.",
    copy:
      "We keep the room generous. Come for a quick bowl at the counter or make an evening of the garden table.",
    cta: "Reserve a table",
    imageAlt:
      "A laid table with linen and seasonal dishes",
    imageBadge: "Table 07 / garden edge",
  },

  menu: {
    sectionNumber: "04",
    marker: "The current menu / Nova Res Culina",
    titleLine1: "Pages",
    titleLine2: "to eat.",
    intro:
      "Move between food and drinks, choose a category, and build your order from the Nova table.",
    foodTab: "Food",
    drinksTab: "Drinks",
    allTab: "All",
    categoryHint: "Select a chapter of the menu",
    currentlyBrowsing: "Currently browsing",
    photographyLine1: "Photography",
    photographyLine2: "coming soon",
    favouriteLabel: "Nova favourite",
    chooseSizeLabel: "Choose a size to add",
    showHighlightsLabel: "Show highlights",
    showFullMenuLabel: "Show full menu",
    exploreAllPrefix: "Explore all",
    itemsLabel: "items",
    footerLabel:
      "Nova Res Culina / Food & drinks / Prices in KES",
    reviewOrderLabel: "Review your order",
  },

  juice: {
    sectionLabel: "04 / refresh",
    titleLine1: "Fresh from",
    titleLine2: "the garden.",
    copy:
      "Pause for something fresh. Cold-pressed fruit, botanical blends, house lemonades and seasonal juices are prepared throughout the day.",
    imageAlt:
      "Fresh juices and fruit drinks at Nova's juice station",
    offerLabel: "Today",
    offerCopy:
      "hibiscus, passion fruit & fresh lime",
  },

  guestBook: {
    sectionNumber: "06",
    marker: "The guest book / Google reviews",
    rating: "4.8",
    reviewCount: "186",
    reviewCountLabel: "Google reviews",
    openLabel: "View all reviews",
    closeLabel: "Close reviews",
  },

  reviews: [
    {
      quote:
        "“The kind of meal that rearranges your week.”",
      name: "Amina W.",
      detail: "Nairobi",
    },
    {
      quote:
        "“Every plate had a point of view. The service had a heartbeat.”",
      name: "Jonas K.",
      detail: "Berlin",
    },
    {
      quote:
        "“The choma, the music, the warm welcome — unforgettable.”",
      name: "Mumbi N.",
      detail: "Nairobi",
    },
  ],

  atNova: {
    sectionNumber: "06",
    eyebrow: "At Nova",
    titleLine1: "More than",
    titleLine2: "a table.",
    copy:
      "The nights, the people, the little rituals and the moments that make Nova feel alive.",
    lead:
      "Some evenings begin with dinner and become something else entirely.",
    body:
      "Game nights, intimate celebrations, beautiful setups, kitchen moments and the energy that fills the room after the first plate arrives.",
    cta: "Plan your own moment",

    reelLabel: "Reels / Nova after hours",
    reelNumber: "01",
    reelTitleLine1: "A night",
    reelTitleLine2: "at Nova.",
    reelCopy:
      "Reels from the floor — plates arriving, music rising, friends settling in and the room becoming its own story.",
    reelFooter: "Nova reels",

    gameLabel: "Game nights / 02",
    gameTitleLine1: "Play after",
    gameTitleLine2: "the plates.",
    gameCopy:
      "Good food, games and just enough competition to keep the whole table talking.",

    celebrationLabel:
      "Setups & celebrations / 03",
    celebrationTitleLine1: "Made for",
    celebrationTitleLine2: "your moment.",
    celebrationCopy:
      "Birthdays, private dinners, corporate gatherings and thoughtful table setups.",

    kitchenLabel: "From the kitchen / 04",
    kitchenCopy:
      "Fire, prep and the hands behind the plate.",

    momentsLabel: "Nova moments / 05",
    momentsCopy:
      "The room, the guests and everything between courses.",
  },

  reservation: {
    sectionNumber: "05",
    eyebrow: "The invitation",
    titleLine1: "Leave room",
    titleLine2: "for one more.",
    copy:
      "Pull up a chair for lunch, a long dinner, or the delivery you order when you want the room to feel a little closer.",

    callLabel: "Call us",
    whatsappLabel: "WhatsApp",

    successEyebrow: "A place is being kept",
    successTitle: "See you at the table.",
    successCopy:
      "Your request has reached the Nova team. We'll review the details and get back to you to confirm the table.",
    successAgain: "Make another reservation",

    formEyebrow: "Reserve a table",
    formTitle: "Make an evening of it.",
    nameLabel: "Your name",
    namePlaceholder: "Nova",
    emailLabel: "Email address",
    emailPlaceholder: "you@email.com",
    dateLabel: "Date",
    timeLabel: "Time",
    guestsLabel: "Guests",
    noteLabel: "A note for the kitchen",
    optionalLabel: "(optional)",
    notePlaceholder:
      "A birthday, a dietary note, a reason to celebrate...",
    sendingLabel: "Sending request…",
    submitLabel: "Request a table",
  },

  location: {
    sectionNumber: "07",
    marker: "The colophon / Find us",
    titleLine1: "Come as",
    titleLine2: "you are.",
    copy:
      "Find us along Ngong Road at TotalEnergies Kerarapon, opposite KCB. Come for the table, stay for the people, and leave with a story.",
    address:
      "TotalEnergies Kerarapon",
    locality: "Ngong Road, opposite KCB",
    hoursLine1: "Tue — Sun",
    hoursLine2: "12:00 — 22:00",
    phone: "+254 719 275522",
    mapNameLine1: "Nova Res",
    mapNameLine2: "Culina",
    mapCoordinates:
      "Kerarapon / Ngong Road",
    mapUrl:
      "https://maps.app.goo.gl/7sTsMRxR6SpeTJrG6",
    mapCta: "Get directions",
  },

  footer: {
    taglineLine1: "Come hungry.",
    taglineLine2: "Leave with a story.",
    findUsLabel: "Find us",
    keepInTouchLabel: "Keep in touch",
    instagramUrl: "https://www.instagram.com/novaresculina/",
    facebookUrl: "#",
    copyright: "© 2024 Nova Res Culina",
    closingLine:
      "Made for the middle of the table",
  },
};

type PublicContentRow = {
  content_key: string;
  value: unknown;
};

function isObject(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function mergeWebsiteContent(
  rows: PublicContentRow[],
): NovaWebsiteContent {
  const next: NovaWebsiteContent =
    structuredClone(
      DEFAULT_NOVA_WEBSITE_CONTENT,
    );

  for (const row of rows) {
    if (!isObject(row.value)) {
      continue;
    }

    switch (row.content_key) {
      case "website.brand":
        next.brand = {
          ...next.brand,
          ...row.value,
        } as NovaWebsiteContent["brand"];
        break;

      case "website.chapters":
        next.chapters = {
          ...next.chapters,
          ...row.value,
        } as NovaWebsiteContent["chapters"];
        break;

      case "website.hero":
        next.hero = {
          ...next.hero,
          ...row.value,
        } as NovaWebsiteContent["hero"];
        break;

      case "website.table":
        next.table = {
          ...next.table,
          ...row.value,
        } as NovaWebsiteContent["table"];
        break;

      case "website.menu":
        next.menu = {
          ...next.menu,
          ...row.value,
        } as NovaWebsiteContent["menu"];
        break;

      case "website.juice":
        next.juice = {
          ...next.juice,
          ...row.value,
        } as NovaWebsiteContent["juice"];
        break;

      case "website.guest_book":
        next.guestBook = {
          ...next.guestBook,
          ...row.value,
        } as NovaWebsiteContent["guestBook"];
        break;

      case "website.reviews":
        if (
          Array.isArray(
            row.value.items,
          )
        ) {
          next.reviews =
            row.value
              .items as NovaWebsiteContent["reviews"];
        }
        break;

      case "website.at_nova":
        next.atNova = {
          ...next.atNova,
          ...row.value,
        } as NovaWebsiteContent["atNova"];
        break;

      case "website.reservation":
        next.reservation = {
          ...next.reservation,
          ...row.value,
        } as NovaWebsiteContent["reservation"];
        break;

      case "website.location":
        next.location = {
          ...next.location,
          ...row.value,
        } as NovaWebsiteContent["location"];
        break;

      case "website.footer":
        next.footer = {
          ...next.footer,
          ...row.value,
        } as NovaWebsiteContent["footer"];
        break;
    }
  }

  return next;
}

export async function loadPublishedNovaWebsiteContent(): Promise<NovaWebsiteContent> {
  const { data, error } =
    await supabase.rpc(
      "get_nova_public_website_content",
    );

  if (error) {
    console.error(
      "[Nova CMS] public website content load failed",
      error,
    );

    return structuredClone(
      DEFAULT_NOVA_WEBSITE_CONTENT,
    );
  }

  return mergeWebsiteContent(
    (Array.isArray(data)
      ? data
      : []) as PublicContentRow[],
  );
}

export type NovaWebsiteContentKey =
  | "website.brand"
  | "website.chapters"
  | "website.hero"
  | "website.table"
  | "website.menu"
  | "website.juice"
  | "website.guest_book"
  | "website.reviews"
  | "website.at_nova"
  | "website.reservation"
  | "website.location"
  | "website.footer";

export type NovaWebsiteEditorDocument = {
  content_key: NovaWebsiteContentKey;
  section_key: string;
  label: string;
  value: Record<string, unknown>;
  hasDraft: boolean;
  updated_at: string | null;
  published_at: string | null;
};

export async function loadNovaWebsiteEditorDocuments(): Promise<
  NovaWebsiteEditorDocument[]
> {
  const { data, error } =
    await supabase.rpc(
      "get_nova_website_editor_documents",
    );

  if (error) {
    throw error;
  }

  return (
    Array.isArray(data)
      ? data
      : []
  ) as NovaWebsiteEditorDocument[];
}

export async function saveNovaWebsiteDraft(
  document: NovaWebsiteEditorDocument,
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error(
      "Nova CMS sign-in required.",
    );
  }

  const { error } = await supabase.rpc(
    "save_nova_cms_draft",
    {
      p_content_key:
        document.content_key,
      p_section_key:
        document.section_key,
      p_label:
        document.label,
      p_value:
        document.value,
    },
  );

  if (error) {
    throw new Error(
      error.message ||
        "Draft could not be saved.",
    );
  }
}

export async function publishNovaWebsiteDocument(
  contentKey: NovaWebsiteContentKey,
): Promise<void> {
  const { error } =
    await supabase.rpc(
      "publish_nova_cms_content",
      {
        p_content_key: contentKey,
      },
    );

  if (error) {
    throw new Error(
      error.message ||
        "Content could not be published.",
    );
  }
}
